import { useEffect, useState } from "react";
import {
  CustomerListing,
  createSellIntent,
  getSellIntent,
} from "../api/payments";

/**
 * SellAskCard — customer-as-seller entry point (09-21).
 *
 * Shown on a project page once a quote exists. Customer picks their ask
 * (1–14%, default 5%) and proposes the design for the marketplace.
 * Admin approves → listing goes live with:
 *   Print price = quote + 20% FOFUS + ask%   (SKU A)
 *   STL price   = quote × 1.35               (SKU B)
 */

const ASK_DEFAULT = 5;
const ASK_MIN = 1;
const ASK_MAX = 14;

export default function SellAskCard({
  projectRef,
  quoteVisible,
}: {
  projectRef: string; // project_number or uuid
  quoteVisible: boolean; // only offer after the customer has a quote
}) {
  const [listing, setListing] = useState<CustomerListing | null>(null);
  const [ask, setAsk] = useState(ASK_DEFAULT);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    if (!quoteVisible) return;
    getSellIntent(projectRef)
      .then((r) => setListing(r.listing))
      .catch(() => {});
  }, [projectRef, quoteVisible]);

  if (!quoteVisible) return null;

  const propose = async () => {
    setBusy(true);
    setErr(null);
    setMsg(null);
    try {
      const r = await createSellIntent(projectRef, { commission_ask_pct: ask });
      setListing(r.listing);
      setMsg(
        r.existing
          ? "Your offer is already in — we'll review it shortly."
          : "Offer sent! FOFUS will review and list your design."
      );
    } catch (e: unknown) {
      const d = (e as { response?: { data?: { detail?: string } } })?.response?.data?.detail;
      setErr(d || "Could not send your offer. Try again.");
    } finally {
      setBusy(false);
    }
  };

  // ── Render by listing state ────────────────────────────────────────────────
  if (listing && (listing.status === "listed" || listing.status === "approved")) {
    return (
      <div className="rounded-xl border border-blue-200 bg-blue-50 p-4">
        <div className="text-sm font-semibold text-blue-900">Your design is LIVE for sale 🎉</div>
        <div className="mt-1 text-xs text-blue-700">
          Print ₹{listing.list_price ?? "—"} · Buy STL ₹{listing.stl_price ?? "—"} · you earn{" "}
          {listing.commission_ask_pct}% on print orders, 80% per STL sale.
        </div>
      </div>
    );
  }

  if (listing && listing.status === "proposed") {
    return (
      <div className="rounded-xl border border-amber-200 bg-amber-50 p-4">
        <div className="text-sm font-semibold text-amber-900">Offer pending review</div>
        <div className="mt-1 text-xs text-amber-700">
          You asked for {listing.commission_ask_pct}% — FOFUS is reviewing. We'll list it with two
          prices: Print and Buy STL.
        </div>
      </div>
    );
  }

  if (listing && listing.status === "declined") {
    return (
      <div className="rounded-xl border border-red-200 bg-red-50 p-4">
        <div className="text-sm font-semibold text-red-900">Offer declined</div>
        {listing.decline_reason && (
          <div className="mt-1 text-xs text-red-700">{listing.decline_reason}</div>
        )}
        <button
          onClick={() => setListing(null)}
          className="mt-2 rounded-lg bg-red-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-red-700"
        >
          Try again
        </button>
      </div>
    );
  }

  // No listing (or paused) → the ask form
  return (
    <div className="rounded-xl border border-gray-200 bg-white p-4">
      <div className="text-sm font-semibold text-gray-900">Earn from your design 💰</div>
      <p className="mt-1 text-xs text-gray-600">
        List it on FOFUS. You set your commission — we handle printing, packing, shipping &
        support.
      </p>
      <div className="mt-3 flex items-center gap-3">
        <label className="text-xs font-medium text-gray-700" htmlFor="ask-slider">
          Your commission: <span className="font-bold text-blue-700">{ask}%</span>
        </label>
        <input
          id="ask-slider"
          type="range"
          min={ASK_MIN}
          max={ASK_MAX}
          value={ask}
          onChange={(e) => setAsk(Number(e.target.value))}
          className="h-2 flex-1 cursor-pointer appearance-none rounded-lg bg-gray-200 accent-blue-600"
        />
      </div>
      <p className="mt-1 text-[11px] text-gray-500">
        Higher ask = more per sale (1–14%). Default 5% sells fastest.
      </p>
      {err && <p className="mt-2 text-xs text-red-600">{err}</p>}
      {msg && <p className="mt-2 text-xs text-green-700">{msg}</p>}
      <button
        onClick={propose}
        disabled={busy}
        className="mt-3 w-full rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-700 disabled:opacity-50"
      >
        {busy ? "Sending…" : `List my design at ${ask}%`}
      </button>
    </div>
  );
}